import { Controller } from "@hotwired/stimulus"

const ASSETS_URL = "https://images.ctfassets.net/bu3up4ijy7vs"

export default class MapMarkerCarouselController extends Controller {
  static values = { id: String, image: String, name: String }
  declare readonly idValue: string
  declare readonly imageValue: string
  declare readonly nameValue: string

  connect(): void {
    this.element.outerHTML = this.carouselView

    void (async (): Promise<void> => {
      await this.requestCarouselImages()
    })()
  }

  private appendCarouselImages(images: string[]): void {
    const carousel = document.getElementById(this.carouselId) as HTMLUListElement

    carousel.insertAdjacentHTML(
      "beforeend",
      images.map((image, index) => this.slideListItem(image, index + 1)).join("")
    )

    Array.from(carousel.children).forEach((child) => {
      child.setAttribute("aria-setsize", (images.length + 1).toString())
    })
  }

  private async requestCarouselImages(): Promise<void> {
    try {
      const result = await fetch(`/search/carousel_images?venue_id=${this.idValue}`)
      const images = (await result.json()) as string[]

      this.appendCarouselImages(images)
    } catch (_) {
      // Ignore fetch errors
    }
  }

  private get carouselId(): string {
    return `carousel-search-map-${this.idValue}`
  }

  private get carouselName(): string {
    return "Search Map"
  }

  private get carouselView(): string {
    const baseIconClasses =
      "fadl [--fa-primary-color:var(--text-color-primary)] [--fa-primary-opacity:.6] [--fa-secondary-color:var(--bg-color-brand-secondary)] [--fa-secondary-opacity:.6] text-24 before:z-1 after:z-1"

    return `
      <div
        class="ui-component-carousel variant:map-marker"
        data-controller="carousel"
      >
        <div>
          <ol
            id="${this.carouselId}"
            aria-live="polite"
            tabindex="0"
            data-carousel-target="slideList"
          >
            ${this.slideListItem(this.imageValue, 0)}
          </ol>

          <button
            type="button"
            aria-label="Previous slide"
            aria-controls="${this.carouselId}"
            data-action="click->carousel#slidePrev"
            data-carousel-target="prevButton"
            data-ga4-click="carousel_click"
            data-ga4-event-group="${this.carouselName}"
            data-ga4-click-target="Previous"
            aria-disabled="true"
          >
            <i class="${baseIconClasses} fa-circle-chevron-left" aria-hidden="true"></i>
          </button>
          <button
            type="button"
            aria-label="Next slide"
            aria-controls="${this.carouselId}"
            data-action="click->carousel#slideNext"
            data-carousel-target="nextButton"
            data-ga4-click="carousel_click"
            data-ga4-event-group="${this.carouselName}"
            data-ga4-click-target="Next"
          >
            <i class="${baseIconClasses} fa-circle-chevron-right" aria-hidden="true"></i>
          </button>
        </div>
      </div>
    `
  }

  private pictureTag(imgSrc: string): string {
    const src = `${ASSETS_URL}${imgSrc}`
    const baseSrc = `${src}?w=327&h=164&fit=fill&fm=avif&q=50`

    return `
      <picture>
        <source
          media="(min-resolution: 1.5x)"
          srcset="${src}?w=654&h=240&fit=fill&fm=avif&q=50 654w"
          sizes="327px"
        >
        <img
          src="${baseSrc}"
          width="327"
          height="120"
          alt="${this.nameValue}"
          fetchpriority="auto"
          class="w-full h-full object-cover"
          loading="lazy"
        >
      </picture>
    `
  }

  private slideListItem(image: string, index: number): string {
    const position = index + 1

    return `
      <li
        aria-roledescription="slide"
        aria-posinset="${position}"
        aria-setsize="1"
        aria-label="Slide ${position}"
        aria-hidden="${position > 1 ? "true" : "false"}"
        data-carousel-target="slideListItem"
        ${position > 2 ? "hidden" : ""}
      >
        ${this.pictureTag(image)}
      </li>
    `
  }
}
